import { markdownToNodes } from '../markdownHelpers';
import { sanitizeInputByType } from '../formatHelpers';
import { apiAiChatCompletions } from '../../api/ai';
import { extractAssistantTextFromOpenRouter } from './aiResponseHelpers';
import { sanitizeSuggestedRename } from '../renameHelpers';

const EXPLORE_ARTIFACT_OPEN_TAG = '<ghost-artifact>';
const EXPLORE_ARTIFACT_CLOSE_TAG = '</ghost-artifact>';
const WRITE_OP_TYPES = new Set(['replace_block', 'insert_after', 'insert_before', 'delete_block']);
const CHAT_STATUS_VARIANTS = new Set(['stage', 'chip']);

const normalizeStatusVariant = (value = null) => {
  const normalized = String(value || '').trim().toLowerCase();
  return CHAT_STATUS_VARIANTS.has(normalized) ? normalized : null;
};

const normalizeChatInputLines = (value = '') =>
  String(value || '')
    .split('\n')
    .map(line => line.trimEnd());

const linesToBlockContent = (lines = []) => {
  const normalized = Array.isArray(lines) ? lines : [];
  const blocks = normalized.map((line) => (
    line.length > 0
      ? {
          type: 'paragraph',
          content: [{ type: 'text', text: line }]
        }
      : { type: 'paragraph' }
  ));

  return blocks.length > 0 ? blocks : [{ type: 'paragraph' }];
};

const applyTextMarksToNodes = (nodes = [], marks = []) => {
  if (!Array.isArray(nodes) || !Array.isArray(marks) || marks.length === 0) {
    return nodes;
  }

  const applyNode = (node) => {
    if (!node || typeof node !== 'object') return node;

    if (typeof node.text === 'string') {
      const existingMarks = Array.isArray(node.marks) ? node.marks : [];
      return {
        ...node,
        marks: [...existingMarks, ...marks]
      };
    }

    if (Array.isArray(node.content)) {
      return {
        ...node,
        content: node.content.map(applyNode)
      };
    }

    return node;
  };

  return nodes.map(applyNode);
};

export const createChatMessageNode = ({
  role = 'user',
  text = '',
  timestamp = Date.now(),
  parseMarkdown = false,
  textMarks = null,
  statusVariant = null,
  progressText = null,
  modelLabel = null,
  requestKey = null,
  artifactText = null
} = {}) => {
  const normalizedText = sanitizeInputByType(text, 'chat');
  const content = parseMarkdown
    ? markdownToNodes(normalizedText)
    : linesToBlockContent(normalizeChatInputLines(normalizedText));
  const markedContent = applyTextMarksToNodes(content, Array.isArray(textMarks) ? textMarks : []);

  return {
    type: 'chatMessage',
    attrs: {
      role,
      timestamp: Number(timestamp) || Date.now(),
      statusVariant: normalizeStatusVariant(statusVariant),
      progressText: sanitizeInputByType(progressText, 'title', { maxLength: 200 }) || null,
      modelLabel: sanitizeInputByType(modelLabel, 'title', { maxLength: 120 }) || null,
      requestKey: sanitizeInputByType(requestKey, 'identifier', { maxLength: 80 }) || null,
      artifactText: sanitizeInputByType(artifactText, 'chat') || null
    },
    content: markedContent
  };
};

export const normalizeChatRole = (role = '') => {
  const normalized = String(role || '').trim().toLowerCase();
  if (normalized === 'ai') return 'assistant';
  if (normalized === 'assistant' || normalized === 'user' || normalized === 'system') return normalized;
  return 'user';
};

export const nodeTextContent = (node) => {
  if (!node || typeof node !== 'object') return '';

  if (typeof node.text === 'string') return node.text;

  if (Array.isArray(node.content)) {
    return node.content.map(child => nodeTextContent(child)).join('');
  }

  return '';
};

const normalizeInlineText = (value = '') => {
  return String(value || '')
    .replace(/\s+/g, ' ')
    .trim();
};

const isChatMessageNode = (node) => {
  return Boolean(node && typeof node === 'object' && node.type === 'chatMessage');
};

export const extractActiveDocContextText = ({ docJson, maxChars = 4000 } = {}) => {
  const limit = Math.max(200, Number(maxChars || 4000));
  const content = Array.isArray(docJson?.content) ? docJson.content : [];

  const nonChatText = content
    .filter(node => !isChatMessageNode(node))
    .map(node => normalizeInlineText(nodeTextContent(node)))
    .filter(Boolean)
    .join('\n\n');

  const fallbackAllText = content
    .map(node => normalizeInlineText(nodeTextContent(node)))
    .filter(Boolean)
    .join('\n\n');

  const source = nonChatText || fallbackAllText;
  if (!source) return '';
  if (source.length <= limit) return source;
  return `${source.slice(0, limit - 3).trimEnd()}...`;
};

export const extractRecentChatQaPairs = ({ docJson, lastQaPairs = 3 } = {}) => {
  const limit = Math.max(1, Number(lastQaPairs || 3));
  const messages = extractChatMessagesFromDocJson({ docJson, maxMessages: 200 });
  const pairs = [];
  let pendingUser = null;

  for (const message of messages) {
    if (message.role === 'user') {
      pendingUser = message;
      continue;
    }

    if (message.role === 'assistant' && pendingUser) {
      pairs.push({ user: pendingUser, assistant: message });
      pendingUser = null;
    }
  }

  return pairs.slice(-limit);
};

export const extractChatMessagesFromDocJson = ({ docJson, maxMessages = 20 } = {}) => {
  const content = Array.isArray(docJson?.content) ? docJson.content : [];
  const messages = [];

  for (const node of content) {
    if (!node || node.type !== 'chatMessage') continue;

    const role = normalizeChatRole(node?.attrs?.role || 'user');
    if (role !== 'user' && role !== 'assistant' && role !== 'system') continue;

    const text = sanitizeInputByType(nodeTextContent(node), 'chat').trim();
    if (!text) continue;

    messages.push({ role, content: text });
  }

  const limit = Math.max(1, Number(maxMessages || 20));
  return messages.slice(-limit);
};

export const getAssistantPlaceholderResponse = ({ userText = '' } = {}) => {
  const prompt = sanitizeInputByType(userText, 'chat').trim();
  if (!prompt) return 'Thinking...';
  return 'Ghost AI is still learning to respond to your messages. Please check back later.';
};

export const appendUserAndAssistantPlaceholder = ({ docJson, userText, now = Date.now() } = {}) => {
  const baseDoc = docJson && typeof docJson === 'object'
    ? docJson
    : { type: 'doc', content: [] };
  const content = Array.isArray(baseDoc.content) ? baseDoc.content : [];

  const trimmed = sanitizeInputByType(userText, 'chat').trim();
  if (!trimmed) return { ...baseDoc, type: 'doc', content };

  const userNode = createChatMessageNode({
    role: 'user',
    text: trimmed,
    timestamp: now
  });

  const assistantNode = createChatMessageNode({
    role: 'ai',
    text: getAssistantPlaceholderResponse({ userText: trimmed }),
    timestamp: Number(now) + 1
  });

  return {
    ...baseDoc,
    type: 'doc',
    content: [...content, userNode, assistantNode]
  };
};

export const normalizeActivityLogOnCompleteMode = (value = 'open') => {
  const normalized = String(value || 'open').trim().toLowerCase();
  if (normalized === 'hidden' || normalized === 'collapsed' || normalized === 'open') return normalized;
  return 'open';
};

export const normalizeChatMode = (mode = 'Explore') => {
  const normalized = String(mode || 'Explore').trim().toLowerCase();
  return normalized === 'write' || normalized === 'edit' ? 'write' : 'explore';
};

export const buildChatLinkedDocIds = ({ orchestration, chatOrchestrationEnabled = false, maxLinkedDocs = 6 } = {}) => {
  if (!chatOrchestrationEnabled) return [];
  const templateDocId = Number(orchestration?.templateDocId || 0);
  const baseIds = Array.isArray(orchestration?.linkedDocIds)
    ? orchestration.linkedDocIds
      .map(id => Number(id))
      .filter(id => Number.isFinite(id) && id > 0)
    : [];
  const prioritized = Number.isFinite(templateDocId) && templateDocId > 0
    ? [templateDocId, ...baseIds]
    : baseIds;
  return Array.from(new Set(prioritized)).slice(0, Math.max(1, Number(maxLinkedDocs || 6)));
};

export const buildChatSystemPrompt = (mode = 'explore') => {
  return mode === 'write'
    ? [
      'You are Ghost AI in Write mode.',
      'Help edit and improve text while preserving user intent and factual grounding.',
      'Prefer precise, scoped edits over full-document rewrites.',
      'When possible, output a JSON object with this shape:',
      '{"write_ops":[{"target_anchor_id":"...","target_text_selector":"...","op_type":"replace_block|insert_after|insert_before|delete_block","new_content":"...","summary_label":"..."}]}'
    ].join(' ')
    : [
      'You are Ghost AI in Explore mode.',
      'Answer clearly and concisely, and use context from prior chat messages when relevant.',
      'When returning copy-paste document output, wrap only that artifact body with',
      `${EXPLORE_ARTIFACT_OPEN_TAG} ... ${EXPLORE_ARTIFACT_CLOSE_TAG}.`
    ].join(' ');
};

const tryParseJsonEnvelopeFromText = (value = '') => {
  const raw = String(value || '').trim();
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') return parsed;
  } catch {
    // Continue with fenced and substring extraction.
  }

  const fencedMatch = raw.match(/```(?:json)?\s*([\s\S]*?)```/i);
  if (fencedMatch?.[1]) {
    try {
      const parsed = JSON.parse(String(fencedMatch[1] || '').trim());
      if (parsed && typeof parsed === 'object') return parsed;
    } catch {
      // Continue with brace extraction.
    }
  }

  const firstBracket = raw.indexOf('[');
  const lastBracket = raw.lastIndexOf(']');
  if (firstBracket >= 0 && lastBracket > firstBracket) {
    try {
      const parsed = JSON.parse(raw.slice(firstBracket, lastBracket + 1));
      if (parsed && typeof parsed === 'object') return parsed;
    } catch {
      // Continue with brace extraction.
    }
  }

  const firstBrace = raw.indexOf('{');
  const lastBrace = raw.lastIndexOf('}');
  if (firstBrace >= 0 && lastBrace > firstBrace) {
    try {
      const parsed = JSON.parse(raw.slice(firstBrace, lastBrace + 1));
      if (parsed && typeof parsed === 'object') return parsed;
    } catch {
      return null;
    }
  }

  return null;
};

const normalizeWriteOp = (value = {}) => {
  const op = value && typeof value === 'object' ? value : {};
  const targetDocIdRaw = Number(op.target_doc_id);
  const targetDocId = Number.isFinite(targetDocIdRaw) && targetDocIdRaw > 0
    ? Math.floor(targetDocIdRaw)
    : null;
  const opType = String(op.op_type || '').trim().toLowerCase();
  const targetAnchorId = String(op.target_anchor_id || '').trim();
  const targetTextSelector = String(op.target_text_selector || '').trim();
  const newContent = String(op.new_content || '').trim();
  const summaryLabel = String(op.summary_label || '').trim();

  const errors = [];
  if (!WRITE_OP_TYPES.has(opType)) {
    errors.push('invalid op_type');
  }
  if (!targetAnchorId) {
    errors.push('missing target_anchor_id');
  }
  if (opType !== 'delete_block' && !newContent) {
    errors.push('missing new_content');
  }

  return {
    valid: errors.length === 0,
    errors,
    op: {
      target_doc_id: targetDocId,
      target_anchor_id: targetAnchorId,
      target_text_selector: targetTextSelector || null,
      op_type: opType,
      new_content: newContent,
      summary_label: summaryLabel || null
    }
  };
};

export const parseAiWriteOperationsEnvelope = (text = '') => {
  const parsed = tryParseJsonEnvelopeFromText(text);
  if (!parsed) {
    return {
      valid: false,
      errors: ['missing_json_envelope'],
      envelope: null,
      operations: []
    };
  }

  const ops = Array.isArray(parsed)
    ? parsed
    : Array.isArray(parsed.write_ops)
      ? parsed.write_ops
      : Array.isArray(parsed.operations)
        ? parsed.operations
        : null;

  if (!Array.isArray(ops) || ops.length === 0) {
    return {
      valid: false,
      errors: ['missing_write_ops'],
      envelope: parsed,
      operations: []
    };
  }

  const normalizedOps = ops.map(normalizeWriteOp);
  const invalid = normalizedOps.filter((entry) => !entry.valid);
  if (invalid.length > 0) {
    return {
      valid: false,
      errors: invalid.flatMap((entry) => entry.errors),
      envelope: parsed,
      operations: normalizedOps.map((entry) => entry.op)
    };
  }

  return {
    valid: true,
    errors: [],
    envelope: parsed,
    operations: normalizedOps.map((entry) => entry.op)
  };
};

const hasWriteOperationsShape = (parsed) => {
  if (!parsed || typeof parsed !== 'object') return false;
  if (Array.isArray(parsed)) return true;
  return Array.isArray(parsed.write_ops) || Array.isArray(parsed.operations);
};

export const isLikelyAiWriteEnvelopeText = (text = '') => {
  const parsed = tryParseJsonEnvelopeFromText(text);
  return hasWriteOperationsShape(parsed);
};

export const getWriteModeFallbackAssistantText = (text = '') => {
  if (isLikelyAiWriteEnvelopeText(text)) {
    return 'I could not apply that write response automatically. No document changes were made.';
  }
  const extracted = extractExploreArtifact(text);
  return extracted?.text || String(text || '').trim();
};

export const extractExploreArtifact = (text = '') => {
  const source = String(text || '');
  if (!source) return { text: '', artifactText: null, hasArtifact: false };

  const openTag = EXPLORE_ARTIFACT_OPEN_TAG;
  const closeTag = EXPLORE_ARTIFACT_CLOSE_TAG;
  const lower = source.toLowerCase();
  const openIndex = lower.indexOf(openTag);
  if (openIndex < 0) {
    return {
      text: source.trim(),
      artifactText: null,
      hasArtifact: false
    };
  }
  const closeIndex = lower.indexOf(closeTag, openIndex + openTag.length);
  if (closeIndex < 0) {
    return {
      text: source.trim(),
      artifactText: null,
      hasArtifact: false
    };
  }

  const artifactStart = openIndex + openTag.length;
  const artifactText = source.slice(artifactStart, closeIndex).trim();
  const before = source.slice(0, openIndex).trim();
  const after = source.slice(closeIndex + closeTag.length).trim();
  const cleaned = [before, after].filter(Boolean).join('\n\n').trim();

  return {
    text: cleaned,
    artifactText: artifactText || null,
    hasArtifact: Boolean(artifactText)
  };
};

export const buildStageSummaryFromEvent = (stageEvent = {}, stageKey = '') => {
  const stageQueries = Array.isArray(stageEvent?.queries)
    ? stageEvent.queries.map(value => String(value || '').trim()).filter(Boolean)
    : [];
  const stageRefs = Array.isArray(stageEvent?.contextReferences) ? stageEvent.contextReferences : [];
  const linkedDocIds = Array.isArray(stageEvent?.linkedDocIds)
    ? stageEvent.linkedDocIds.map(value => Number(value)).filter(value => Number.isFinite(value) && value > 0)
    : [];
  const stageIntent = String(stageEvent?.intentKey || '').trim();
  const stageIntentConfidence = Number(stageEvent?.intentConfidence || 0);
  const retrievalDecision = String(stageEvent?.retrievalDecision || '').trim();
  const selectedCount = Number(stageEvent?.selectedCount || 0);
  const summarizedChunkCount = Number(stageEvent?.summarizedChunkCount || 0);
  const summarizedDocCount = Number(stageEvent?.summarizedDocCount || 0);
  const candidateCount = Number(stageEvent?.candidateCount || 0);
  const queryCount = Number(stageEvent?.queryCount || stageQueries.length || 0);
  const usageTotalTokens = Number(stageEvent?.usage?.totalTokens || 0);
  const totalCostUsd = Number(stageEvent?.cost?.totalCostUsd || 0);
  const sourceBreakdown = stageEvent?.selectedSourceBreakdown && typeof stageEvent.selectedSourceBreakdown === 'object'
    ? stageEvent.selectedSourceBreakdown
    : {};

  const summaryParts = [];
  if (stageIntent) {
    const confidencePct = stageIntentConfidence > 0 ? Math.round(stageIntentConfidence * 100) : null;
    summaryParts.push(confidencePct ? `intent=${stageIntent} (${confidencePct}%)` : `intent=${stageIntent}`);
  }
  if (queryCount > 0) summaryParts.push(`queries=${queryCount}`);
  if (retrievalDecision) summaryParts.push(`retrieval=${retrievalDecision}`);
  if (candidateCount > 0) summaryParts.push(`candidates=${candidateCount}`);
  if (selectedCount > 0) summaryParts.push(`selected=${selectedCount}`);
  if (summarizedChunkCount > 0 || summarizedDocCount > 0) {
    summaryParts.push(`summarized=${summarizedChunkCount} chunks/${summarizedDocCount} docs`);
  }
  if (linkedDocIds.length > 0) summaryParts.push(`linkedDocs=${linkedDocIds.length}`);
  if (usageTotalTokens > 0) summaryParts.push(`tokens=${usageTotalTokens}`);
  if (totalCostUsd > 0) summaryParts.push(`cost=$${totalCostUsd.toFixed(4)}`);
  if (Object.keys(sourceBreakdown).length > 0) {
    const sourceLabels = Object.entries(sourceBreakdown)
      .map(([key, count]) => `${String(key)}:${Number(count || 0)}`)
      .slice(0, 3);
    if (sourceLabels.length > 0) summaryParts.push(`sources=${sourceLabels.join(', ')}`);
  } else if (stageRefs.length > 0 && stageKey === 'retrieve') {
    summaryParts.push('sources=active context assembled');
  }

  return summaryParts.join(' ; ');
};

export const classifyAiChatError = (err, latestStreamPreview = '') => {
  const message = String(err?.message || '');
  const code = String(err?.code || '');
  const isTimeoutError = code === 'AI_TIMEOUT' || /timed out|timeout/i.test(message);
  const isPolicyBlocked = code === 'OPENROUTER_POLICY_BLOCKED'
    || /guardrail restrictions|data policy|openrouter\.ai\/settings\/privacy/i.test(message);
  const isRateLimited = Number(err?.status || 0) === 429
    || /too many requests|rate limit|provider returned error/i.test(message);
  const isNetworkError = code === 'AI_FETCH_FAILED'
    || /failed to fetch|networkerror|load failed|network request failed/i.test(message);

  const fallbackText = String(latestStreamPreview || '').trim() || (isPolicyBlocked
    ? 'AI request is blocked by privacy or guardrail settings. Please review OpenRouter privacy settings and try again.'
    : isTimeoutError
      ? 'The AI response took too long and timed out. Please try again.'
    : isRateLimited
      ? 'AI is currently rate-limited. Please wait a moment and try again.'
      : isNetworkError
        ? 'I could not reach the AI service right now. Please check your connection and try again.'
        : 'AI could not complete this request. Please try again.');

  const toastMessage = isPolicyBlocked
    ? 'AI blocked by OpenRouter privacy/guardrail settings. Check OpenRouter privacy settings.'
    : isTimeoutError
      ? 'AI response timed out. Please retry.'
    : isRateLimited
      ? 'AI is currently rate-limited by OpenRouter. Please wait and try again.'
      : isNetworkError
        ? 'Could not reach AI service. Check your internet connection and try again.'
        : 'Could not connect to AI. Please try again.';

  return { isPolicyBlocked, isTimeoutError, isRateLimited, isNetworkError, fallbackText, toastMessage };
};
export const requestChatRenameSuggestion = async ({
  model,
  userPrompt = '',
  assistantResponse = '',
  signal
} = {}) => {
  const trimmedPrompt = String(userPrompt || '').trim();
  const trimmedResponse = String(assistantResponse || '').trim();
  if (!trimmedPrompt || !trimmedResponse) return '';

  const response = await apiAiChatCompletions({
    model,
    temperature: 0.2,
    max_tokens: 32,
    signal,
    messages: [
      {
        role: 'system',
        content: 'Return only a concise chat title of 2 to 6 words. No quotes. No punctuation unless essential. No markdown. Prefer specificity over generic labels.'
      },
      {
        role: 'user',
        content: [
          `Initial prompt: ${trimmedPrompt}`,
          `Assistant response: ${trimmedResponse}`
        ].join('\n\n')
      }
    ]
  });

  return sanitizeSuggestedRename(extractAssistantTextFromOpenRouter(response), { maxLength: 80 });
};
